import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Navbar from "@/components/layout/Navbar";
import CorporateList from "@/components/corporates/CorporateList";
import TariffApprovalHub from "@/components/tariffs/TariffApprovalHub";
import AnalyticsDashboard from "@/components/analytics/AnalyticsDashboard";
import UserManagement from "@/components/users/UserManagement";
import DeviceApprovals from "@/components/security/DeviceApprovals";
import ActivityLog from "@/components/security/ActivityLog";
import CustomAreas from "@/components/locations/CustomAreas";
import { Building2, CheckSquare, BarChart3, Users, MonitorSmartphone, History } from "lucide-react";

export default function Portal() {
  const { user, isManagement } = useAuth();
  const [tab, setTab] = useState("corporates");
  const [properties, setProperties] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingDevices, setPendingDevices] = useState(0);

  const loadNotifications = useCallback(() => {
    api.get("/notifications").then((r) => setNotifications(r.data)).catch(() => {});
    if (isManagement) {
      api.get("/analytics").then((r) => setPendingCount(r.data.tariff_counts?.Pending || 0)).catch(() => {});
      api.get("/devices", { params: { status: "pending" } }).then((r) => setPendingDevices(r.data.length)).catch(() => {});
    }
  }, [isManagement]);

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
    loadNotifications();
  }, [loadNotifications]);


  const tabs = [
    { id: "corporates", label: "Corporate Master", icon: Building2, show: true },
    { id: "approvals", label: "Tariff Approvals", icon: CheckSquare, show: true, badge: isManagement ? pendingCount : 0 },
    { id: "analytics", label: "Analytics", icon: BarChart3, show: isManagement },
    { id: "users", label: "User Management", icon: Users, show: isManagement },
    { id: "devices", label: "Device Approvals", icon: MonitorSmartphone, show: isManagement, badge: pendingDevices },
    { id: "activity", label: "Activity Log", icon: History, show: isManagement },
  ].filter((t) => t.show);

  return (
    <div className="min-h-screen bg-background">
      <Navbar notifications={notifications} reloadNotifications={loadNotifications} />

      <div className="border-b border-border bg-card/60 backdrop-blur-md sticky top-16 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <nav className="flex gap-1 overflow-x-auto">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`relative flex items-center gap-2 px-4 py-3.5 text-sm font-medium whitespace-nowrap transition-colors ${
                    active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                  {t.badge > 0 && (
                    <span className="ml-1 text-[10px] font-mono bg-primary text-primary-foreground rounded-full px-1.5 py-0.5">{t.badge}</span>
                  )}
                  {active && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" />}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        {tab === "corporates" && <CorporateList properties={properties} reloadNotifications={loadNotifications} />}
        {tab === "approvals" && <TariffApprovalHub properties={properties} reloadNotifications={loadNotifications} />}
        {tab === "analytics" && isManagement && <AnalyticsDashboard />}
        {tab === "users" && isManagement && <div className="space-y-8"><UserManagement /><CustomAreas /></div>}
        {tab === "devices" && isManagement && <DeviceApprovals onChanged={loadNotifications} />}
        {tab === "activity" && isManagement && <ActivityLog />}
      </main>
    </div>
  );
}
